/**
 * Chat store for managing active chat state using Zustand.
 *
 * Manages:
 * - Active chat ID
 * - Draft message content
 * - Per-chat streaming state (keyed by chatId for chat isolation)
 * - Pending actions
 * - Selected knowledge bases per chat (ephemeral, not persisted)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { SelectedKnowledgeBase } from '@/components/chat/KnowledgeBasePill';
import type { GeneratedImageData } from '@/components/chat/GeneratedImage';
import { appendSegment, EMPTY_SEGMENTS, type MessageSegment } from '@/lib/messageSegments';

/**
 * LLM mode type for model selection.
 */
export type LlmMode = 'auto' | 'heavy' | 'fast' | 'custom';

/**
 * Pending action types for optimistic UI updates.
 */
export type PendingActionType = 'create' | 'update' | 'delete' | 'send_message' | 'regenerate';

/**
 * Pending action with metadata.
 */
export interface PendingAction {
  /** Unique action ID */
  id: string;
  /** Action type */
  type: PendingActionType;
  /** Target entity ID (chat ID or message ID) */
  targetId: string;
  /** Timestamp when action was initiated */
  timestamp: number;
  /** Optional additional data */
  data?: Record<string, unknown>;
}

/**
 * Research progress status from crew status events.
 */
export interface ResearchStatus {
  sources_completed: number;
  sources_total: number;
  sources_failed: number;
  phase?: string | undefined;
  phase_label?: string | undefined;
  phase_step?: number | undefined;
  phase_total?: number | undefined;
  phase_progress?: number | undefined;
  phase_progress_total?: number | undefined;
}

/**
 * Lifecycle phase for a streaming chat.
 */
export type StreamPhase = 'connecting' | 'streaming' | 'disconnected' | 'following';

/**
 * Set when this tab FOLLOWS a turn another client (or an earlier load of
 * this app) started: there is no SSE stream here, only polls of
 * `GET /chats/{id}/live`. The phase is then 'following'.
 */
export interface FollowInfo {
  /** The server stream id (X-Stream-ID of the original request). */
  serverStreamId: string;
  /** 'new': a live placeholder row; 'append': a browser continuation. */
  mode: 'new' | 'append';
  /** The stream has not written for over a minute (writer gone or wedged). */
  stale: boolean;
}

/** What `adoptServerStream` needs to start following a live turn. */
export interface AdoptServerStreamArgs {
  /** The live row (new) or the continuation target (append). */
  messageId: string;
  serverStreamId: string;
  mode: 'new' | 'append';
  /** For append mode: the target's own content and segments. */
  append: StreamingAppendBase | null;
  /** The in progress answer text so far. */
  content: string;
  /** The in progress segments so far. */
  segments: MessageSegment[];
}

/**
 * What a user (or a timeout) decided about one parked tool call.
 *
 * `pending` is the only state that renders buttons. Every other value is
 * terminal, which is what stops a card from hanging: the orchestrator gives up
 * and denies on its own after `timeout_seconds`, so a card the user never
 * answered has to reach `expired` rather than wait forever.
 *
 * `unavailable` is the degraded state. The decision endpoints answer 404 while
 * the feature is switched off server side, and that is the CURRENT production
 * state, so a 404 has to read as "this cannot be answered here" rather than as
 * a failure the user did something to cause.
 *
 * `unknown` is the honest state. An answer that times out AFTER the backend
 * accepted it looks, on retry, exactly like an answer that never arrived: the
 * call is no longer parked either way. Reporting that as `expired` would tell
 * the user Andi left a write undone when it may well have run, so the case
 * where the outcome genuinely cannot be known gets its own state and says so.
 */
export type ApprovalState =
  | 'pending'
  | 'allowed_once'
  | 'allowed_always'
  | 'allowed_run'
  | 'denied'
  | 'expired'
  | 'unavailable'
  | 'unknown'
  | 'answered_elsewhere';

/**
 * The approval half of a tool call, present only when one was requested.
 */
export interface ConnectorApproval {
  state: ApprovalState;
  /**
   * Whether the card may offer "always". False for every sensitive builtin
   * write, which are single request approvals by design.
   */
  allowAlwaysEligible: boolean;
  /**
   * Whether the card may offer "Allow the rest of this request" (a write
   * gate card, spec S11.6). Absent means false.
   */
  allowRunEligible?: boolean;
  /** Epoch ms at which the orchestrator stops waiting. */
  expiresAt: number;
  /** True once the standing permission was actually recorded server side. */
  grantPersisted: boolean;
  /**
   * True while this screen has an answer to the card in flight. A followed
   * turn's poll leaves such a card alone: the server may already have dropped
   * it from its pending list because of the very answer being sent.
   */
  submitting?: boolean;
  /**
   * Followed turn: consecutive counted `/live` polls that omitted this open
   * card. One miss can be a transient read failure (possibly paired with a
   * stale `awaiting_approval: false`), so a card settles only on the second.
   */
  missedPolls?: number;
}

/**
 * One tool call in the live transcript, keyed by the server minted call id.
 *
 * A single record covers both frames and the card, because the orchestrator
 * mints ONE id per call and puts it on all three. The record is built by
 * whichever frame arrives first, so nothing here may assume an order.
 */
export interface ConnectorCallRecord {
  /** Server minted id. Correlates the card, the call and the result. */
  callId: string;
  /** Connector display name. Empty for a builtin write. */
  connector: string;
  /**
   * Connector row id, or null for a sensitive builtin write.
   *
   * Never rendered. It is kept because it is the only reliable way to tell a
   * connector call from a builtin write, and the two get different copy and a
   * different button set.
   */
  connectorId: number | null;
  /**
   * "crew" when a crew's write gate raised this card (spec S11.6), with
   * `connectorId` null. Such a card is titled by the crew and never shows the
   * tool name. Empty or absent for the orchestrator's own frames.
   */
  origin?: string;
  /** Raw server side tool name. Shown as detail, never as the headline. */
  tool: string;
  /** What would be sent. The reason this feature exists. */
  arguments: Record<string, unknown> | null;
  /** Approval state, or null when this call never needed one. */
  approval: ConnectorApproval | null;
  /**
   * Outcome, once the result frame arrives.
   *
   * `summaryCode` is the machine outcome the client translates for itself;
   * `summary` is the server's own English prose, kept because an older
   * orchestrator sends no code, and because for `remote_error` the prose is
   * the remote server's message and is the thing worth showing.
   */
  result: { ok: boolean; summary: string; summaryCode: string } | null;
  /** True once the call frame arrived, i.e. the call actually went out. */
  called: boolean;
  /** Epoch ms the record was created, for stable ordering. */
  createdAt: number;
}

/**
 * What an append mode stream (a browser continuation) is appended to: the
 * existing assistant message's content and segments when the stream began.
 * The live content and segments of the stream are shown after these.
 */
export interface StreamingAppendBase {
  baseContent: string;
  baseSegments: MessageSegment[];
}

/** Options for `startStreaming`. Omitted for an ordinary new reply. */
export interface StartStreamingOptions {
  mode: 'append';
  baseContent: string;
  baseSegments: MessageSegment[];
}

/**
 * Per-chat streaming state. Presence of a chat key in `streamingByChat`
 * indicates the chat is currently streaming.
 */
export interface StreamingState {
  /** ID of the message being streamed (for real-time updates) */
  streamingMessageId: string;
  /** Accumulator for streamed response content */
  streamingContent: string;
  /**
   * This stream's answer and steps segments in stream order, built from
   * content and reasoning deltas alike. Only rendered when a steps segment
   * exists, so an ordinary reply looks exactly as before.
   */
  streamingSegments: MessageSegment[];
  /** Set for a browser continuation streaming into an existing message. */
  append: StreamingAppendBase | null;
  /** Research progress status (from crew SSE status events) */
  researchStatus: ResearchStatus | null;
  /**
   * Status events received this turn. Incremented by `setResearchStatus` in
   * the same update that stores the event, so a heartbeat that repeats an
   * unchanged phase label still changes something observable, and it changes
   * in the same render as the label (see ChatTurnStatus `data-status-seq`).
   */
  statusSeq: number;
  /** Generated images received during streaming */
  streamingGeneratedImages: GeneratedImageData[] | null;
  /** Whether the current stream is from the image-generator crew */
  isImageGeneration: boolean;
  /** Lifecycle phase of the stream */
  phase: StreamPhase;
  /** Set only while following a server side turn (phase 'following'). */
  follow: FollowInfo | null;
  /**
   * Browser sessions this turn uses, from web-backend's `turn_browser` frame
   * (pinned browser panel anchoring). Empty until one arrives.
   */
  turnBrowserHandles: string[];
  /** A `browser_*` status arrived this turn (sticky for the turn). */
  turnUsesBrowser: boolean;
}

/**
 * Chat store state interface.
 */
interface ChatState {
  // ============================================================================
  // State
  // ============================================================================

  /** Currently active chat ID */
  activeChatId: string | null;

  /** Draft message content per chat (preserved across navigation) */
  drafts: Record<string, string>;

  /**
   * Per-chat streaming state. The presence of a chat key indicates that chat
   * is streaming. Each entry holds the message ID, accumulated content,
   * research status, generated images, and lifecycle phase for that chat.
   */
  streamingByChat: Record<string, StreamingState>;

  /**
   * Live connector tool calls and approval cards, keyed by chatId.
   *
   * LIVE ONLY. These frames are never persisted anywhere, by decision, so the
   * transcript loses them on reload. That is why they sit here rather than in
   * the streaming entry: the streaming entry is deleted the moment a turn
   * ends, and a record of what was sent to a third party server that vanishes
   * the instant the answer appears would defeat the point of showing it. The
   * list is cleared when the NEXT turn starts.
   */
  connectorActivityByChat: Record<string, ConnectorCallRecord[]>;

  /** Pending actions awaiting server confirmation */
  pendingActions: PendingAction[];

  /** Last error that occurred during chat operations */
  lastError: string | null;

  /** Selected LLM mode preference */
  llmMode: LlmMode;

  /** Custom model ID for custom mode (OpenRouter-format, e.g. "anthropic/claude-sonnet-4") */
  customModelId: string | null;

  /** Custom temperature for custom mode (default 0.7) */
  customTemperature: number;

  /** Private mode flag - session-only, NOT persisted to localStorage */
  privateMode: boolean;

  /** Selected knowledge bases per chat (ephemeral, keyed by chatId) */
  selectedKnowledgeBases: Record<string, SelectedKnowledgeBase[]>;

  // ============================================================================
  // Actions
  // ============================================================================

  /** Set the LLM mode */
  setLlmMode: (mode: LlmMode) => void;

  /** Set the active chat ID */
  setActiveChatId: (chatId: string | null) => void;

  /** Get draft content for a specific chat */
  getDraft: (chatId: string) => string;

  /** Set draft content for a specific chat */
  setDraft: (chatId: string, content: string) => void;

  /** Clear draft content for a specific chat */
  clearDraft: (chatId: string) => void;

  /** Clear all drafts */
  clearAllDrafts: () => void;

  /**
   * Start streaming for a specific chat. Initializes a fresh streaming entry
   * keyed by chatId. Replaces any existing entry for that chat.
   */
  startStreaming: (chatId: string, messageId: string, options?: StartStreamingOptions) => void;

  /**
   * Append content to the streaming message for a specific chat.
   * No-op if the chat has no streaming entry (protects against late events
   * arriving after stopStreaming).
   */
  appendStreamingContent: (chatId: string, content: string) => void;

  /**
   * Append reasoning (the agent's narration) to the streaming message's
   * steps segments. Never touches `streamingContent`. No-op without an entry.
   */
  appendStreamingReasoning: (chatId: string, reasoning: string) => void;

  /**
   * Replace streaming content for a specific chat (for 'replace' SSE events).
   * No-op if the chat has no streaming entry.
   */
  replaceStreamingContent: (chatId: string, content: string) => void;

  /** Stop streaming for a specific chat (removes the chat's entry) */
  stopStreaming: (chatId: string) => void;

  /**
   * Follow a turn that is streaming server side with no SSE stream in this
   * tab (a reopened app). Replaces any entry for the chat, like
   * startStreaming, and clears the previous turn's connector records.
   */
  adoptServerStream: (chatId: string, args: AdoptServerStreamArgs) => void;

  /**
   * Replace a followed turn's text and segments with the latest server copy.
   * No-op unless the chat is following.
   */
  applyServerStreamSnapshot: (
    chatId: string,
    snapshot: { content: string; segments: MessageSegment[]; stale: boolean }
  ) => void;

  /**
   * Point the streaming entry at a new message id (the server id announced
   * by `stream_started`). No-op unless the entry streams `fromId`.
   */
  rekeyStreamingMessage: (chatId: string, fromId: string, toId: string) => void;

  /**
   * Main chat browser resume answered `X-Andi-Resume-Placement: new_row`:
   * the continuation is written as a NEW row at the bottom instead of being
   * appended to the card. Turn the append mode entry into an ordinary
   * streaming entry keyed by `newMessageId` (a fresh temporary id when null),
   * keeping what streamed so far. Idempotent: an entry that is not in append
   * mode is left alone. Returns the entry's message id, or null without an
   * entry.
   */
  convertAppendToNew: (chatId: string, newMessageId: string | null) => string | null;

  /** Record the `turn_browser` handles of a chat's current turn. */
  setTurnBrowserHandles: (chatId: string, handles: string[]) => void;

  /**
   * Set research progress status for a specific chat.
   * No-op if the chat has no streaming entry.
   */
  setResearchStatus: (chatId: string, status: ResearchStatus) => void;

  /**
   * Clear research progress status for a specific chat.
   * No-op if the chat has no streaming entry.
   */
  clearResearchStatus: (chatId: string) => void;

  /**
   * Set generated images during streaming for a specific chat.
   * No-op if the chat has no streaming entry.
   */
  setStreamingGeneratedImages: (chatId: string, images: GeneratedImageData[]) => void;

  /**
   * Mark the current stream for a specific chat as image generation.
   * No-op if the chat has no streaming entry.
   */
  setStreamingIsImageGeneration: (chatId: string, value: boolean) => void;

  /**
   * Set the lifecycle phase for a chat's stream.
   * No-op if the chat has no streaming entry.
   */
  setStreamPhase: (chatId: string, phase: StreamPhase) => void;

  /**
   * Record a connector tool call frame. Merges into an existing record when
   * the approval card for the same call id arrived first.
   */
  recordConnectorToolCall: (
    chatId: string,
    call: {
      callId: string;
      connector: string;
      tool: string;
      arguments: Record<string, unknown> | null;
    }
  ) => void;

  /**
   * Record the outcome of a connector tool call. Ignored when no record with
   * that call id exists, since a result nothing can be attached to would
   * render as an orphan block with no idea what it is the result of.
   */
  recordConnectorToolResult: (
    chatId: string,
    callId: string,
    result: { ok: boolean; summary: string; summaryCode: string }
  ) => void;

  /** Record an approval card. Merges with the call frame of the same id. */
  recordApprovalRequest: (
    chatId: string,
    request: {
      callId: string;
      connector: string;
      connectorId: number | null;
      tool: string;
      arguments: Record<string, unknown> | null;
      allowAlwaysEligible: boolean;
      /** "crew" for a crew write-gate card; empty otherwise. */
      origin?: string;
      allowRunEligible?: boolean;
      expiresAt: number;
    }
  ) => void;

  /** Move one card to a terminal state (a decision, a timeout, or a 404). */
  setApprovalState: (
    chatId: string,
    callId: string,
    state: ApprovalState,
    options?: { grantPersisted?: boolean }
  ) => void;

  /**
   * Resolve every still open card for a chat, without ending the stream.
   *
   * For the one end of turn path that cannot call `stopStreaming`: a stalled
   * stream keeps its streaming entry so recovery can poll it, but its cards
   * can no longer be answered and must not keep offering to be.
   */
  expirePendingApprovals: (chatId: string) => void;

  /**
   * Followed turn: reconcile open cards with the server's pending list (see
   * {@link settleApprovalsIn}): settle the ones it reliably no longer lists
   * to `answered_elsewhere` or `expired`, and reopen ones it lists again.
   * Leaves state identical when nothing changes.
   */
  settleApprovalsNotPending: (
    chatId: string,
    pendingCallIds: ReadonlySet<string>,
    options?: SettleApprovalsOptions
  ) => void;

  /** Mark one card as having (or no longer having) an answer in flight here. */
  setApprovalSubmitting: (chatId: string, callId: string, submitting: boolean) => void;

  /** Drop every connector record for a chat. */
  clearConnectorActivity: (chatId: string) => void;

  /** Add a pending action */
  addPendingAction: (action: Omit<PendingAction, 'id' | 'timestamp'>) => string;

  /** Remove a pending action by ID */
  removePendingAction: (actionId: string) => void;

  /** Check if there's a pending action for a target */
  hasPendingAction: (targetId: string, type?: PendingActionType) => boolean;

  /** Get pending actions for a target */
  getPendingActions: (targetId: string) => PendingAction[];

  /** Set the last error */
  setLastError: (error: string | null) => void;

  /** Reset the store to initial state */
  reset: () => void;

  // ============================================================================
  // Custom Model Actions
  // ============================================================================

  /** Set custom model settings (model ID and temperature) */
  setCustomSettings: (settings: { modelId: string; temperature: number }) => void;

  /** Clear custom model settings, resetting to defaults and switching to auto mode */
  clearCustomSettings: () => void;

  /** Set private mode (session-only, not persisted) */
  setPrivateMode: (enabled: boolean) => void;

  // ============================================================================
  // Knowledge Base Actions
  // ============================================================================

  /** Add a knowledge base to the selection for a chat */
  addKnowledgeBase: (chatId: string, kb: SelectedKnowledgeBase) => void;

  /** Remove a knowledge base from the selection for a chat */
  removeKnowledgeBase: (chatId: string, kbId: string) => void;

  /** Clear all selected knowledge bases for a chat */
  clearKnowledgeBases: (chatId: string) => void;

  /** Get selected knowledge bases for a chat */
  getSelectedKnowledgeBases: (chatId: string) => SelectedKnowledgeBase[];

  /** Set all selected knowledge bases for a chat (for loading from backend) */
  setKnowledgeBases: (chatId: string, kbs: SelectedKnowledgeBase[]) => void;
}

/**
 * Initial state values.
 */
const initialState = {
  activeChatId: null,
  drafts: {},
  streamingByChat: {} as Record<string, StreamingState>,
  connectorActivityByChat: {} as Record<string, ConnectorCallRecord[]>,
  pendingActions: [],
  lastError: null,
  llmMode: 'auto' as LlmMode,
  customModelId: null as string | null,
  customTemperature: 0.7,
  privateMode: false,
  selectedKnowledgeBases: {},
};

/**
 * Generate a unique action ID.
 */
function generateActionId(): string {
  return `action-${String(Date.now())}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Helper: update a single chat's streaming state if it exists.
 * No-op if the chat has no streaming entry — protects against late events.
 */
function updateChatStream(
  state: { streamingByChat: Record<string, StreamingState> },
  chatId: string,
  patch: Partial<StreamingState>
): { streamingByChat: Record<string, StreamingState> } | null {
  const existing = state.streamingByChat[chatId];
  if (!existing) return null;
  return {
    streamingByChat: {
      ...state.streamingByChat,
      [chatId]: { ...existing, ...patch },
    },
  };
}

/** Makes temporary ids of converted continuations unique within a millisecond. */
let newContinuationCounter = 0;

/** Shared empty handle list (stable identity for selectors). */
const EMPTY_HANDLES: string[] = [];

/** Whether two segment lists are equal by value. */
function sameSegments(a: readonly MessageSegment[], b: readonly MessageSegment[]): boolean {
  return (
    a.length === b.length &&
    a.every((segment, i) => {
      const other = b[i];
      return other !== undefined && segment.kind === other.kind && segment.text === other.text;
    })
  );
}

/**
 * Helper: apply a patch to one connector record, matched by call id.
 * Returns null when the chat has no record with that id.
 */
function updateConnectorRecord(
  state: { connectorActivityByChat: Record<string, ConnectorCallRecord[]> },
  chatId: string,
  callId: string,
  patch: (record: ConnectorCallRecord) => ConnectorCallRecord
): { connectorActivityByChat: Record<string, ConnectorCallRecord[]> } | null {
  const records = state.connectorActivityByChat[chatId];
  if (!records?.some((record) => record.callId === callId)) return null;
  return {
    connectorActivityByChat: {
      ...state.connectorActivityByChat,
      [chatId]: records.map((record) => (record.callId === callId ? patch(record) : record)),
    },
  };
}

/**
 * Helper: move every still open card for a chat to `expired`.
 *
 * Returns the replacement map, or null when there was nothing open, so the
 * callers can leave state identical rather than churn a new object.
 *
 * Factored out because TWO end of turn paths need it and only one of them can
 * use `stopStreaming`. A stalled stream deliberately keeps its streaming entry
 * alive for recovery, so it cannot go through `stopStreaming`, but the run it
 * was parked on is just as over: leaving live buttons there lets a user who
 * comes back to a backgrounded tab tap Allow and fire a write at a third party
 * server while the browser shows them only a disconnect.
 */
function expirePendingApprovalsIn(
  state: { connectorActivityByChat: Record<string, ConnectorCallRecord[]> },
  chatId: string
): Record<string, ConnectorCallRecord[]> | null {
  const records = state.connectorActivityByChat[chatId];
  if (!records?.some((record) => record.approval?.state === 'pending')) {
    return null;
  }
  return {
    ...state.connectorActivityByChat,
    [chatId]: records.map((record) =>
      record.approval?.state === 'pending'
        ? { ...record, approval: { ...record.approval, state: 'expired' as const } }
        : record
    ),
  };
}

/** How far a followed turn's pending approval list can be trusted. */
export interface SettleApprovalsOptions {
  /**
   * The stream's `awaiting_approval` flag. Only an explicit `false` makes an
   * EMPTY list worth counting: `/live` answers `[]` when its Redis read fails,
   * while the flag comes from the database row and can still say true. Even
   * `false` never settles a card on its own, since it can be stale (the writer
   * stops flushing after MAX_WRITER_SECONDS and legacy rows report false).
   */
  awaitingApproval?: boolean | undefined;
  /** Epoch ms, for tests. Defaults to `Date.now()`. */
  now?: number;
}

/** Consecutive absent polls a card must reach before it settles. */
export const APPROVAL_SETTLE_MISSES = 2;

/**
 * Helper: reconcile a followed chat's cards with the server's pending list.
 *
 * - A card the server lists again after it was settled as answered elsewhere
 *   reopens (the settle was based on a list that later proved incomplete).
 * - An open card the server omits settles to `expired` at once when its
 *   deadline has passed. Otherwise it settles to `answered_elsewhere` only
 *   after being missing from {@link APPROVAL_SETTLE_MISSES} consecutive polls
 *   whose list counts: an empty list while the stream may still await an
 *   approval (`awaitingApproval` true or unknown) says nothing and is not
 *   counted. A single miss never settles, because an empty list from a failed
 *   Redis read paired with a stale `awaitingApproval: false` looks identical
 *   to a genuine answer elsewhere.
 * - A card with an answer in flight here is left alone.
 *
 * Returns the replacement list, or null when nothing changes.
 */
export function settleApprovalsIn(
  records: ConnectorCallRecord[],
  pendingCallIds: ReadonlySet<string>,
  options: SettleApprovalsOptions = {}
): ConnectorCallRecord[] | null {
  const now = options.now ?? Date.now();
  const awaitingFalse = options.awaitingApproval === false;
  const listTrusted = pendingCallIds.size > 0 || awaitingFalse;
  const next = records.map((record) => {
    const approval = record.approval;
    if (!approval) return record;
    const listed = pendingCallIds.has(record.callId);
    if (listed) {
      if (approval.state === 'answered_elsewhere') {
        return { ...record, approval: { ...approval, state: 'pending' as const, missedPolls: 0 } };
      }
      if (approval.state === 'pending' && approval.missedPolls) {
        return { ...record, approval: { ...approval, missedPolls: 0 } };
      }
      return record;
    }
    if (approval.state !== 'pending' || approval.submitting) return record;
    if (approval.expiresAt <= now) {
      return { ...record, approval: { ...approval, state: 'expired' as const } };
    }
    if (!listTrusted) return record;
    const misses = (approval.missedPolls ?? 0) + 1;
    if (misses >= APPROVAL_SETTLE_MISSES) {
      return {
        ...record,
        approval: { ...approval, state: 'answered_elsewhere' as const, missedPolls: 0 },
      };
    }
    return { ...record, approval: { ...approval, missedPolls: misses } };
  });
  return next.some((record, i) => record !== records[i]) ? next : null;
}

/**
 * Helper: insert or merge a connector record for one call id.
 *
 * The card frame and the call frame carry the same id and can arrive in
 * either order, so both entry points go through this rather than one
 * appending and the other assuming a record already exists.
 */
function upsertConnectorRecord(
  state: { connectorActivityByChat: Record<string, ConnectorCallRecord[]> },
  chatId: string,
  callId: string,
  seed: () => ConnectorCallRecord,
  merge: (record: ConnectorCallRecord) => ConnectorCallRecord
): { connectorActivityByChat: Record<string, ConnectorCallRecord[]> } {
  const records = state.connectorActivityByChat[chatId] ?? [];
  const next = records.some((record) => record.callId === callId)
    ? records.map((record) => (record.callId === callId ? merge(record) : record))
    : [...records, seed()];
  return {
    connectorActivityByChat: {
      ...state.connectorActivityByChat,
      [chatId]: next,
    },
  };
}

/**
 * Chat store with Zustand for active chat state management.
 *
 * This store manages ephemeral UI state for chat interactions,
 * while server state (chat data, messages) is managed by TanStack Query.
 *
 * Streaming state is keyed by chatId so concurrent streams across multiple
 * chats remain isolated.
 *
 * @example
 * ```tsx
 * function ChatView({ chatId }: Props) {
 *   const isStreaming = useIsStreaming(chatId);
 *   const setActiveChatId = useChatStore((state) => state.setActiveChatId);
 *
 *   useEffect(() => {
 *     setActiveChatId(chatId);
 *     return () => setActiveChatId(null);
 *   }, [chatId, setActiveChatId]);
 *
 *   return (
 *     <div>
 *       {isStreaming && <StreamingIndicator />}
 *       <MessageList chatId={chatId} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setLlmMode: (mode) => set({ llmMode: mode }),

      setActiveChatId: (chatId) => set({ activeChatId: chatId }),

      getDraft: (chatId) => get().drafts[chatId] ?? '',

      setDraft: (chatId, content) =>
        set((state) => ({
          drafts: {
            ...state.drafts,
            [chatId]: content,
          },
        })),

      clearDraft: (chatId) =>
        set((state) => {
          const { [chatId]: _removed, ...remainingDrafts } = state.drafts;
          void _removed; // Explicitly mark as intentionally unused
          return { drafts: remainingDrafts };
        }),

      clearAllDrafts: () => set({ drafts: {} }),

      startStreaming: (chatId, messageId, options) =>
        set((state) => {
          // A new turn clears the previous turn's connector records. They are
          // deliberately kept past the end of their own turn (see
          // connectorActivityByChat) but they belong to THAT turn, so they go
          // when the next one starts.
          const { [chatId]: _priorActivity, ...remainingActivity } = state.connectorActivityByChat;
          void _priorActivity;
          return {
            connectorActivityByChat: remainingActivity,
            streamingByChat: {
              ...state.streamingByChat,
              [chatId]: {
                streamingMessageId: messageId,
                streamingContent: '',
                streamingSegments: EMPTY_SEGMENTS,
                append:
                  options?.mode === 'append'
                    ? { baseContent: options.baseContent, baseSegments: options.baseSegments }
                    : null,
                researchStatus: null,
                statusSeq: 0,
                streamingGeneratedImages: null,
                isImageGeneration: false,
                phase: 'connecting',
                follow: null,
                turnBrowserHandles: EMPTY_HANDLES,
                turnUsesBrowser: false,
              },
            },
          };
        }),

      appendStreamingContent: (chatId, content) =>
        set((state) => {
          const existing = state.streamingByChat[chatId];
          if (!existing) return state;
          return {
            streamingByChat: {
              ...state.streamingByChat,
              [chatId]: {
                ...existing,
                streamingContent: existing.streamingContent + content,
                streamingSegments: appendSegment(existing.streamingSegments, 'answer', content),
              },
            },
          };
        }),

      appendStreamingReasoning: (chatId, reasoning) =>
        set((state) => {
          const existing = state.streamingByChat[chatId];
          if (!existing) return state;
          return {
            streamingByChat: {
              ...state.streamingByChat,
              [chatId]: {
                ...existing,
                streamingSegments: appendSegment(existing.streamingSegments, 'steps', reasoning),
              },
            },
          };
        }),

      replaceStreamingContent: (chatId, content) =>
        set((state) => updateChatStream(state, chatId, { streamingContent: content }) ?? state),

      stopStreaming: (chatId) =>
        set((state) => {
          if (!(chatId in state.streamingByChat)) return state;
          const { [chatId]: _removed, ...remaining } = state.streamingByChat;
          void _removed;
          // Any card still open when the turn ends can no longer be answered:
          // the run this one is parked on is over, and the orchestrator has
          // already resolved its own wait as a deny. Resolving them here, in
          // the action every clean end path goes through (done, error, abort),
          // is what guarantees no card is left showing live buttons over a
          // turn that has finished. The stall path cannot come through here,
          // because it has to keep the streaming entry alive for recovery, so
          // it calls `expirePendingApprovals` directly instead.
          const expired = expirePendingApprovalsIn(state, chatId);
          if (!expired) {
            return { streamingByChat: remaining };
          }
          return { streamingByChat: remaining, connectorActivityByChat: expired };
        }),

      adoptServerStream: (chatId, args) =>
        set((state) => {
          const { [chatId]: _priorActivity, ...remainingActivity } = state.connectorActivityByChat;
          void _priorActivity;
          return {
            connectorActivityByChat: remainingActivity,
            streamingByChat: {
              ...state.streamingByChat,
              [chatId]: {
                streamingMessageId: args.messageId,
                streamingContent: args.content,
                streamingSegments: args.segments,
                append: args.append,
                researchStatus: null,
                statusSeq: 0,
                streamingGeneratedImages: null,
                isImageGeneration: false,
                phase: 'following',
                follow: { serverStreamId: args.serverStreamId, mode: args.mode, stale: false },
                turnBrowserHandles: EMPTY_HANDLES,
                turnUsesBrowser: false,
              },
            },
          };
        }),

      applyServerStreamSnapshot: (chatId, snapshot) =>
        set((state) => {
          const existing = state.streamingByChat[chatId];
          if (!existing?.follow) return state;
          if (
            existing.streamingContent === snapshot.content &&
            existing.follow.stale === snapshot.stale &&
            sameSegments(existing.streamingSegments, snapshot.segments)
          ) {
            return state;
          }
          return {
            streamingByChat: {
              ...state.streamingByChat,
              [chatId]: {
                ...existing,
                streamingContent: snapshot.content,
                streamingSegments: snapshot.segments,
                follow: { ...existing.follow, stale: snapshot.stale },
              },
            },
          };
        }),

      setTurnBrowserHandles: (chatId, handles) =>
        set((state) => updateChatStream(state, chatId, { turnBrowserHandles: handles }) ?? state),

      convertAppendToNew: (chatId, newMessageId) => {
        const existing = get().streamingByChat[chatId];
        if (!existing) return null;
        if (!existing.append) return existing.streamingMessageId;
        newContinuationCounter += 1;
        const id =
          newMessageId ?? `temp-${String(Date.now())}-cont-${String(newContinuationCounter)}`;
        set(
          (state) =>
            updateChatStream(state, chatId, { append: null, streamingMessageId: id }) ?? state
        );
        return id;
      },

      rekeyStreamingMessage: (chatId, fromId, toId) =>
        set((state) => {
          const existing = state.streamingByChat[chatId];
          if (existing?.streamingMessageId !== fromId || fromId === toId) return state;
          return updateChatStream(state, chatId, { streamingMessageId: toId }) ?? state;
        }),

      expirePendingApprovals: (chatId) =>
        set((state) => {
          const expired = expirePendingApprovalsIn(state, chatId);
          return expired ? { connectorActivityByChat: expired } : state;
        }),

      settleApprovalsNotPending: (chatId, pendingCallIds, options) =>
        set((state) => {
          const records = state.connectorActivityByChat[chatId];
          if (!records) return state;
          const next = settleApprovalsIn(records, pendingCallIds, options);
          if (!next) return state;
          return {
            connectorActivityByChat: { ...state.connectorActivityByChat, [chatId]: next },
          };
        }),

      setApprovalSubmitting: (chatId, callId, submitting) =>
        set(
          (state) =>
            updateConnectorRecord(state, chatId, callId, (record) =>
              record.approval && (record.approval.submitting ?? false) !== submitting
                ? { ...record, approval: { ...record.approval, submitting } }
                : record
            ) ?? state
        ),

      setResearchStatus: (chatId, status) =>
        set(
          (state) =>
            updateChatStream(state, chatId, {
              researchStatus: status,
              statusSeq: (state.streamingByChat[chatId]?.statusSeq ?? 0) + 1,
              ...(status.phase?.startsWith('browser_') ? { turnUsesBrowser: true } : {}),
            }) ?? state
        ),

      clearResearchStatus: (chatId) =>
        set((state) => updateChatStream(state, chatId, { researchStatus: null }) ?? state),

      setStreamingGeneratedImages: (chatId, images) =>
        set(
          (state) => updateChatStream(state, chatId, { streamingGeneratedImages: images }) ?? state
        ),

      setStreamingIsImageGeneration: (chatId, value) =>
        set((state) => updateChatStream(state, chatId, { isImageGeneration: value }) ?? state),

      setStreamPhase: (chatId, phase) =>
        set((state) => updateChatStream(state, chatId, { phase }) ?? state),

      recordConnectorToolCall: (chatId, call) =>
        set((state) =>
          upsertConnectorRecord(
            state,
            chatId,
            call.callId,
            () => ({
              callId: call.callId,
              connector: call.connector,
              // A connector tool call frame carries no row id, and it does not
              // need one: only the approval card branches on it, and a card
              // for this id would have seeded the record with the real value.
              connectorId: null,
              tool: call.tool,
              arguments: call.arguments,
              approval: null,
              result: null,
              called: true,
              createdAt: Date.now(),
            }),
            (record) => ({
              ...record,
              called: true,
              // The card and the call frame carry the same arguments, so this
              // fills a gap rather than overwriting a better value.
              connector: record.connector || call.connector,
              tool: record.tool || call.tool,
              arguments: record.arguments ?? call.arguments,
            })
          )
        ),

      recordConnectorToolResult: (chatId, callId, result) =>
        set(
          (state) =>
            updateConnectorRecord(state, chatId, callId, (record) => ({
              ...record,
              result: {
                ok: result.ok,
                summary: result.summary,
                summaryCode: result.summaryCode,
              },
            })) ?? state
        ),

      recordApprovalRequest: (chatId, request) =>
        set((state) =>
          upsertConnectorRecord(
            state,
            chatId,
            request.callId,
            () => ({
              callId: request.callId,
              connector: request.connector,
              connectorId: request.connectorId,
              origin: request.origin ?? '',
              tool: request.tool,
              arguments: request.arguments,
              approval: {
                state: 'pending',
                allowAlwaysEligible: request.allowAlwaysEligible,
                allowRunEligible: request.allowRunEligible === true,
                expiresAt: request.expiresAt,
                grantPersisted: false,
              },
              result: null,
              called: false,
              createdAt: Date.now(),
            }),
            (record) => ({
              ...record,
              connector: request.connector || record.connector,
              connectorId: request.connectorId,
              origin: request.origin || record.origin || '',
              tool: request.tool || record.tool,
              arguments: request.arguments ?? record.arguments,
              // A second card for a call already decided does not reopen it.
              // One settled only because a followed poll stopped listing it
              // does: the server listing it again says it is still open.
              approval:
                record.approval && record.approval.state !== 'answered_elsewhere'
                  ? record.approval
                  : {
                      state: 'pending',
                      allowAlwaysEligible: request.allowAlwaysEligible,
                      allowRunEligible: request.allowRunEligible === true,
                      expiresAt: request.expiresAt,
                      grantPersisted: false,
                    },
            })
          )
        ),

      setApprovalState: (chatId, callId, approvalState, options) =>
        set(
          (state) =>
            updateConnectorRecord(state, chatId, callId, (record) =>
              record.approval
                ? {
                    ...record,
                    approval: {
                      ...record.approval,
                      state: approvalState,
                      grantPersisted: options?.grantPersisted ?? record.approval.grantPersisted,
                    },
                  }
                : record
            ) ?? state
        ),

      clearConnectorActivity: (chatId) =>
        set((state) => {
          if (!(chatId in state.connectorActivityByChat)) return state;
          const { [chatId]: _removed, ...remaining } = state.connectorActivityByChat;
          void _removed;
          return { connectorActivityByChat: remaining };
        }),

      addPendingAction: (action) => {
        const id = generateActionId();
        const fullAction: PendingAction = {
          ...action,
          id,
          timestamp: Date.now(),
        };

        set((state) => ({
          pendingActions: [...state.pendingActions, fullAction],
        }));

        return id;
      },

      removePendingAction: (actionId) =>
        set((state) => ({
          pendingActions: state.pendingActions.filter((a) => a.id !== actionId),
        })),

      hasPendingAction: (targetId, type) => {
        const actions = get().pendingActions;
        return actions.some(
          (a) => a.targetId === targetId && (type === undefined || a.type === type)
        );
      },

      getPendingActions: (targetId) => get().pendingActions.filter((a) => a.targetId === targetId),

      setLastError: (error) => set({ lastError: error }),

      reset: () => set(initialState),

      // ========================================================================
      // Custom Model Actions
      // ========================================================================

      setCustomSettings: (settings) =>
        set({
          customModelId: settings.modelId,
          customTemperature: settings.temperature,
        }),

      clearCustomSettings: () =>
        set({
          customModelId: null,
          customTemperature: 0.7,
          llmMode: 'auto',
        }),

      setPrivateMode: (enabled) => set({ privateMode: enabled }),

      // ========================================================================
      // Knowledge Base Actions
      // ========================================================================

      addKnowledgeBase: (chatId, kb) =>
        set((state) => {
          const currentKbs = state.selectedKnowledgeBases[chatId] ?? [];
          // Check if already added (prevent duplicates)
          if (currentKbs.some((existing) => existing.id === kb.id)) {
            return state;
          }
          return {
            selectedKnowledgeBases: {
              ...state.selectedKnowledgeBases,
              [chatId]: [...currentKbs, kb],
            },
          };
        }),

      removeKnowledgeBase: (chatId, kbId) =>
        set((state) => {
          const currentKbs = state.selectedKnowledgeBases[chatId] ?? [];
          const filtered = currentKbs.filter((kb) => kb.id !== kbId);
          // If empty array, remove the key entirely
          if (filtered.length === 0) {
            const { [chatId]: _removed, ...remaining } = state.selectedKnowledgeBases;
            void _removed;
            return { selectedKnowledgeBases: remaining };
          }
          return {
            selectedKnowledgeBases: {
              ...state.selectedKnowledgeBases,
              [chatId]: filtered,
            },
          };
        }),

      clearKnowledgeBases: (chatId) =>
        set((state) => {
          const { [chatId]: _removed, ...remaining } = state.selectedKnowledgeBases;
          void _removed;
          return { selectedKnowledgeBases: remaining };
        }),

      getSelectedKnowledgeBases: (chatId) => get().selectedKnowledgeBases[chatId] ?? [],

      setKnowledgeBases: (chatId, kbs) =>
        set((state) => {
          if (kbs.length === 0) {
            // If empty, remove the key
            const { [chatId]: _removed, ...remaining } = state.selectedKnowledgeBases;
            void _removed;
            return { selectedKnowledgeBases: remaining };
          }
          return {
            selectedKnowledgeBases: {
              ...state.selectedKnowledgeBases,
              [chatId]: kbs,
            },
          };
        }),
    }),
    {
      name: 'chat-storage',
      partialize: (state) => ({
        // Persist drafts, LLM mode, and custom model settings across sessions
        // NOTE: selectedKnowledgeBases is NOT persisted - it's ephemeral per session
        // NOTE: privateMode is NOT persisted - it's session-only and resets to false on reload
        // NOTE: streamingByChat is NOT persisted - it's transient stream state
        // NOTE: connectorActivityByChat is NOT persisted - connector frames are
        //       live only by decision, so a reload starts with none and the
        //       transcript simply has no tool blocks rather than stale ones
        drafts: state.drafts,
        llmMode: state.llmMode,
        customModelId: state.customModelId,
        customTemperature: state.customTemperature,
      }),
    }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Get the active chat ID.
 */
export const useActiveChatId = () => useChatStore((state) => state.activeChatId);

/**
 * Whether the given chat is currently streaming.
 */
export const useIsStreaming = (chatId: string | null | undefined): boolean =>
  useChatStore((state) => (chatId ? chatId in state.streamingByChat : false));

/**
 * Whether ANY chat is currently streaming (e.g. for sidebar dot indicator).
 */
export const useAnyStreaming = (): boolean =>
  useChatStore((state) => Object.keys(state.streamingByChat).length > 0);

/**
 * Get the streaming message ID for the given chat (null if not streaming).
 */
export const useStreamingMessageId = (chatId: string | null | undefined): string | null =>
  useChatStore((state) =>
    chatId ? (state.streamingByChat[chatId]?.streamingMessageId ?? null) : null
  );

/**
 * Get the accumulated streaming content for the given chat (empty string if not streaming).
 */
export const useStreamingContent = (chatId: string | null | undefined): string =>
  useChatStore((state) => (chatId ? (state.streamingByChat[chatId]?.streamingContent ?? '') : ''));

/**
 * This stream's segments for the given chat (a stable empty list when none).
 */
export const useStreamingSegments = (chatId: string | null | undefined): MessageSegment[] =>
  useChatStore((state) =>
    chatId ? (state.streamingByChat[chatId]?.streamingSegments ?? EMPTY_SEGMENTS) : EMPTY_SEGMENTS
  );

/**
 * The append base of a browser continuation streaming in the given chat, or
 * null for an ordinary reply (or no stream).
 */
export const useStreamingAppend = (chatId: string | null | undefined): StreamingAppendBase | null =>
  useChatStore((state) => (chatId ? (state.streamingByChat[chatId]?.append ?? null) : null));

/**
 * Get the current research progress status for the given chat (null if not streaming).
 */
export const useResearchStatus = (chatId: string | null | undefined): ResearchStatus | null =>
  useChatStore((state) =>
    chatId ? (state.streamingByChat[chatId]?.researchStatus ?? null) : null
  );

/**
 * Number of status events the given chat's current turn has received (0 when
 * not streaming). Advances on every event, including a repeated identical one.
 */
export const useStatusSeq = (chatId: string | null | undefined): number =>
  useChatStore((state) => (chatId ? (state.streamingByChat[chatId]?.statusSeq ?? 0) : 0));

/**
 * Shared empty list so a chat with no connector activity returns the SAME
 * reference on every render. Zustand v5 compares the selector's result by
 * identity, and a fresh `[]` per call would re-render on every store change.
 */
const EMPTY_CONNECTOR_ACTIVITY: ConnectorCallRecord[] = [];

/**
 * Live connector tool calls and approval cards for the given chat.
 *
 * Empty for every chat that has no connectors in play, which is all of them
 * while the feature is off, so the renderer costs nothing until it is on.
 */
export const useConnectorActivity = (chatId: string | null | undefined): ConnectorCallRecord[] =>
  useChatStore((state) =>
    chatId
      ? (state.connectorActivityByChat[chatId] ?? EMPTY_CONNECTOR_ACTIVITY)
      : EMPTY_CONNECTOR_ACTIVITY
  );

/**
 * Get generated images received during streaming for the given chat.
 */
export const useStreamingGeneratedImages = (
  chatId: string | null | undefined
): GeneratedImageData[] | null =>
  useChatStore((state) =>
    chatId ? (state.streamingByChat[chatId]?.streamingGeneratedImages ?? null) : null
  );

/**
 * Get whether the current stream for the given chat is image generation.
 */
export const useStreamingIsImageGeneration = (chatId: string | null | undefined): boolean =>
  useChatStore((state) =>
    chatId ? (state.streamingByChat[chatId]?.isImageGeneration ?? false) : false
  );

/**
 * Get the lifecycle phase for the given chat's stream (null if not streaming).
 */
export const useStreamPhase = (chatId: string | null | undefined): StreamPhase | null =>
  useChatStore((state) => (chatId ? (state.streamingByChat[chatId]?.phase ?? null) : null));

/**
 * Follow details for the given chat (null unless following a server turn).
 */
export const useFollowInfo = (chatId: string | null | undefined): FollowInfo | null =>
  useChatStore((state) => (chatId ? (state.streamingByChat[chatId]?.follow ?? null) : null));

/**
 * Get the draft content for a specific chat.
 */
export const useDraft = (chatId: string) => useChatStore((state) => state.drafts[chatId] ?? '');

/**
 * Check if there are any pending actions.
 */
export const useHasPendingActions = () => useChatStore((state) => state.pendingActions.length > 0);

/**
 * Get the selected LLM mode.
 */
export const useLlmMode = () => useChatStore((state) => state.llmMode);

/**
 * Get the custom model ID.
 */
export const useCustomModelId = () => useChatStore((state) => state.customModelId);

/**
 * Get the custom temperature.
 */
export const useCustomTemperature = () => useChatStore((state) => state.customTemperature);

/**
 * Get the private mode state.
 */
export const usePrivateMode = () => useChatStore((state) => state.privateMode);

/**
 * Get the last error.
 */
export const useLastChatError = () => useChatStore((state) => state.lastError);

/**
 * Stable empty array for selector to avoid infinite re-renders.
 * @internal
 */
const EMPTY_KNOWLEDGE_BASES: SelectedKnowledgeBase[] = [];

/**
 * Get selected knowledge bases for a specific chat.
 * Returns a stable empty array reference when no KBs are selected.
 */
export const useSelectedKnowledgeBases = (chatId: string) =>
  useChatStore((state) => state.selectedKnowledgeBases[chatId] ?? EMPTY_KNOWLEDGE_BASES);

// ============================================================================
// Action Hooks (for cleaner component usage)
// ============================================================================

/**
 * Get chat store actions without subscribing to state changes.
 *
 * Use this when you only need to dispatch actions without reading state.
 *
 * @example
 * ```tsx
 * function SendButton({ chatId }: Props) {
 *   const { clearDraft, startStreaming } = useChatActions();
 *
 *   const handleSend = () => {
 *     startStreaming(chatId, 'new-msg-id');
 *     clearDraft(chatId);
 *   };
 * }
 * ```
 */
export const useChatActions = () => ({
  setLlmMode: useChatStore.getState().setLlmMode,
  setActiveChatId: useChatStore.getState().setActiveChatId,
  getDraft: useChatStore.getState().getDraft,
  setDraft: useChatStore.getState().setDraft,
  clearDraft: useChatStore.getState().clearDraft,
  clearAllDrafts: useChatStore.getState().clearAllDrafts,
  startStreaming: useChatStore.getState().startStreaming,
  appendStreamingContent: useChatStore.getState().appendStreamingContent,
  appendStreamingReasoning: useChatStore.getState().appendStreamingReasoning,
  replaceStreamingContent: useChatStore.getState().replaceStreamingContent,
  stopStreaming: useChatStore.getState().stopStreaming,
  setResearchStatus: useChatStore.getState().setResearchStatus,
  clearResearchStatus: useChatStore.getState().clearResearchStatus,
  setStreamingGeneratedImages: useChatStore.getState().setStreamingGeneratedImages,
  setStreamingIsImageGeneration: useChatStore.getState().setStreamingIsImageGeneration,
  setStreamPhase: useChatStore.getState().setStreamPhase,
  addPendingAction: useChatStore.getState().addPendingAction,
  removePendingAction: useChatStore.getState().removePendingAction,
  hasPendingAction: useChatStore.getState().hasPendingAction,
  getPendingActions: useChatStore.getState().getPendingActions,
  setLastError: useChatStore.getState().setLastError,
  reset: useChatStore.getState().reset,
  // Custom model actions
  setCustomSettings: useChatStore.getState().setCustomSettings,
  clearCustomSettings: useChatStore.getState().clearCustomSettings,
  setPrivateMode: useChatStore.getState().setPrivateMode,
  // Knowledge base actions
  addKnowledgeBase: useChatStore.getState().addKnowledgeBase,
  removeKnowledgeBase: useChatStore.getState().removeKnowledgeBase,
  clearKnowledgeBases: useChatStore.getState().clearKnowledgeBases,
  getSelectedKnowledgeBases: useChatStore.getState().getSelectedKnowledgeBases,
  setKnowledgeBases: useChatStore.getState().setKnowledgeBases,
});
